import EventEmitter from 'eventemitter3';
import * as awarenessProtocol from 'y-protocols/awareness';
import type * as Y from 'yjs';

import { type Capabilities } from '@canvas/capability';
import {
  AwarenessChannel,
  type AwarenessClientIDs,
  type AwarenessContainerEvents,
  type CanvasAwareness,
} from '@canvas/model/awareness';
import { MessageKey, MessageType } from '@canvas/protocol';
import { AwarenessEncodingType, type AwarenessMessage } from '@canvas/protocol-types';
import { type TransactionOrigin, YJS_DEFAULT, YjsOrigin } from '@canvas/transaction';

import type { CollabStrategy } from '../collab-strategy/index.ts';

import { AwarenessDataHandler } from './awareness-data-handler.ts';

const allAwarenessChannels = Object.keys(AwarenessChannel)
  .map((key) => Number(key))
  .filter((x): x is AwarenessChannel => !isNaN(x));

const allAwarenessChannelsMask = allAwarenessChannels.reduce(
  (mask, channel) => mask | channel,
  0,
);

type AwarenessMeasurementBase = Readonly<{
  transport: CollabStrategy['type'];
  observedMembership: number;
  batchSize: number;
}>;

export type AwarenessMeasurement =
  | (AwarenessMeasurementBase & Readonly<{ kind: 'sent'; encodedBytes: number }>)
  | (AwarenessMeasurementBase & Readonly<{ kind: 'received' }>);

class SingleAwarenessChannel {
  public readonly awareness: awarenessProtocol.Awareness;

  public constructor(
    public readonly channel: AwarenessChannel,
    private emitEvent: EventEmitter<AwarenessContainerEvents>['emit'],
    doc: Y.Doc,
  ) {
    this.awareness = new awarenessProtocol.Awareness(doc);
    this.awareness.on('update', this.awarenessUpdateHandler);
  }

  private awarenessUpdateHandler = (clientIDs: AwarenessClientIDs, origin: any) => {
    this.emitEvent('update', this.channel, clientIDs, origin);
  };

  public destroy() {
    this.awareness.off('update', this.awarenessUpdateHandler);
    this.awareness.destroy();
  }
}

/**
 * Contains and manages multiple different awareness channels.
 */
export class AwarenessContainer implements CanvasAwareness {
  private readonly transientTransactionAwareness: SingleAwarenessChannel;
  private readonly telepointerAwareness: SingleAwarenessChannel;
  private readonly participantsAwareness: SingleAwarenessChannel;
  private readonly selectionAwareness: SingleAwarenessChannel;
  private readonly smartLinkAwareness: SingleAwarenessChannel;
  private readonly cameraAwareness: SingleAwarenessChannel;
  private readonly typingAwareness: SingleAwarenessChannel;
  private readonly racingAwareness: SingleAwarenessChannel;
  private readonly slidesAwareness: SingleAwarenessChannel;

  private readonly allAwareness: SingleAwarenessChannel[];

  private readonly events: EventEmitter<AwarenessContainerEvents> =
    new EventEmitter<AwarenessContainerEvents>();
  private destroyListeners: VoidFunction[] = [];

  public emitter = this.events.emit.bind(this.events);

  // Throttling-related properties
  private readonly THROTTLED_CHANNELS = new Set([
    AwarenessChannel.Selection,
    AwarenessChannel.TransientTransaction,
  ]);
  private readonly MIN_THROTTLE_INTERVAL = 100; // 100ms
  private readonly MAX_THROTTLE_INTERVAL = 2000; // 2s
  private lastThrottledUpdateTime = 0;

  private readonly dataHandler: AwarenessDataHandler;
  private readonly onMeasurement?: (measurement: AwarenessMeasurement) => void;

  public constructor(
    doc: Y.Doc,
    onMeasurement?: (measurement: AwarenessMeasurement) => void,
  ) {
    this.dataHandler = new AwarenessDataHandler();
    this.onMeasurement = onMeasurement;

    this.transientTransactionAwareness = new SingleAwarenessChannel(
      AwarenessChannel.TransientTransaction,
      this.emitter,
      doc,
    );
    this.telepointerAwareness = new SingleAwarenessChannel(
      AwarenessChannel.Telepointer,
      this.emitter,
      doc,
    );
    this.participantsAwareness = new SingleAwarenessChannel(
      AwarenessChannel.Participants,
      this.emitter,
      doc,
    );
    this.selectionAwareness = new SingleAwarenessChannel(
      AwarenessChannel.Selection,
      this.emitter,
      doc,
    );

    this.smartLinkAwareness = new SingleAwarenessChannel(
      AwarenessChannel.SmartLinks,
      this.emitter,
      doc,
    );
    this.cameraAwareness = new SingleAwarenessChannel(
      AwarenessChannel.Camera,
      this.emitter,
      doc,
    );
    this.typingAwareness = new SingleAwarenessChannel(
      AwarenessChannel.Typing,
      this.emitter,
      doc,
    );
    this.racingAwareness = new SingleAwarenessChannel(
      AwarenessChannel.Racing,
      this.emitter,
      doc,
    );
    this.slidesAwareness = new SingleAwarenessChannel(
      AwarenessChannel.Slides,
      this.emitter,
      doc,
    );

    this.allAwareness = [
      this.transientTransactionAwareness,
      this.telepointerAwareness,
      this.participantsAwareness,
      this.selectionAwareness,
      this.smartLinkAwareness,
      this.cameraAwareness,
      this.typingAwareness,
      this.racingAwareness,
      this.slidesAwareness,
    ];
  }

  public on = this.events.on.bind(this.events);
  public off = this.events.off.bind(this.events);

  /**
   * Returns the awareness object for a given awareness channel.
   */
  public getAwareness(channel: AwarenessChannel): awarenessProtocol.Awareness {
    switch (channel) {
      case AwarenessChannel.TransientTransaction:
        return this.transientTransactionAwareness.awareness;
      case AwarenessChannel.Telepointer:
        return this.telepointerAwareness.awareness;
      case AwarenessChannel.Participants:
        return this.participantsAwareness.awareness;
      case AwarenessChannel.Selection:
        return this.selectionAwareness.awareness;
      case AwarenessChannel.SmartLinks:
        return this.smartLinkAwareness.awareness;
      case AwarenessChannel.Camera:
        return this.cameraAwareness.awareness;
      case AwarenessChannel.Typing:
        return this.typingAwareness.awareness;
      case AwarenessChannel.Racing:
        return this.racingAwareness.awareness;
      case AwarenessChannel.Slides:
        return this.slidesAwareness.awareness;
    }
  }

  /**
   * Returns the awareness message key for a given awareness channel.
   */
  public getAwarenessMessageKey(
    channel: AwarenessChannel,
  ): keyof AwarenessMessage['data'] {
    switch (channel) {
      case AwarenessChannel.TransientTransaction:
        return 'transientTransactionData';
      case AwarenessChannel.Telepointer:
        return 'telepointerData';
      case AwarenessChannel.Participants:
        return 'participantData';
      case AwarenessChannel.Selection:
        return 'selectionData';
      case AwarenessChannel.SmartLinks:
        return 'smartLinksUpdateData';
      case AwarenessChannel.Camera:
        return 'cameraData';
      case AwarenessChannel.Typing:
        return 'typingData';
      case AwarenessChannel.Racing:
        return 'racingData';
      case AwarenessChannel.Slides:
        return 'slidesData';
    }
  }

  /**
   * Returns the awareness channel for a given awareness message key.
   */
  public getAwarenessChannel(key: string): AwarenessChannel | null {
    switch (key) {
      case 'transientTransactionData':
        return AwarenessChannel.TransientTransaction;
      case 'telepointerData':
        return AwarenessChannel.Telepointer;
      case 'participantData':
        return AwarenessChannel.Participants;
      case 'selectionData':
        return AwarenessChannel.Selection;
      case 'smartLinksUpdateData':
        return AwarenessChannel.SmartLinks;
      case 'cameraData':
        return AwarenessChannel.Camera;
      case 'typingData':
        return AwarenessChannel.Typing;
      case 'racingData':
        return AwarenessChannel.Racing;
      case 'slidesData':
        return AwarenessChannel.Slides;
    }

    return null;
  }

  private filterMessage(
    channel: AwarenessChannel,
    consume: boolean,
    capabilities?: Capabilities,
  ) {
    if (!capabilities) {
      return true;
    }
    switch (channel) {
      case AwarenessChannel.Selection:
        return consume
          ? capabilities.awareness.selections.canConsume
          : capabilities.awareness.selections.canSend;
      case AwarenessChannel.Telepointer:
        return consume
          ? capabilities.awareness.telepointer.canConsume
          : capabilities.awareness.telepointer.canSend;
      case AwarenessChannel.TransientTransaction:
        return consume
          ? capabilities.awareness.transient.canConsume
          : capabilities.awareness.transient.canSend;
      default:
        return true;
    }
  }

  /**
   * Returns the number of unique collaborators across all awareness channels.
   */
  public getNumberOfCollaborators(): number {
    const uniqueClients = new Set<number>();
    for (const channel of this.allAwareness) {
      channel.awareness.getStates().forEach((_, clientId) => uniqueClients.add(clientId));
    }
    return uniqueClients.size;
  }

  private emitMeasurement(measurement: AwarenessMeasurement): void {
    try {
      this.onMeasurement?.(measurement);
      // eslint-disable-next-line canvas/no-swallow-errors-in-catch -- Measurement failures must not interrupt collaboration.
    } catch {
      // A failing observer must not block collaboration traffic.
    }
  }

  public recordSent(
    message: AwarenessMessage,
    encodedBytes: number,
    transport: CollabStrategy['type'],
  ): void {
    if (this.onMeasurement) {
      this.emitMeasurement({
        kind: 'sent',
        transport,
        observedMembership: this.getNumberOfCollaborators(),
        batchSize: Object.keys(message.data).length,
        encodedBytes,
      });
    }
  }

  public recordReceived(
    message: AwarenessMessage,
    transport: CollabStrategy['type'],
  ): void {
    if (this.onMeasurement) {
      this.emitMeasurement({
        kind: 'received',
        transport,
        observedMembership: this.getNumberOfCollaborators(),
        batchSize: Object.keys(message.data).length,
      });
    }
  }
  /**
   * Calculates the collaborator interval multiplier based on number of collaborators.
   * The multiplier scales with N * (N-1) where N is number of collaborators.
   * This represents the number of messages that would be sent in a fully connected network.
   *
   * Examples:
   * 1 collaborator: max(1, 1 * 0) - 1 = 0    (no messages needed)
   * 2 collaborators: max(1, 2 * 1) - 1 = 1   (2 messages: a→b, b→a)
   * 3 collaborators: max(1, 3 * 2) - 1 = 5   (6 messages: a→b|c, b→a|c, c→a|b)
   * 4 collaborators: max(1, 4 * 3) - 1 = 11  (12 messages: a→b|c|d, b→a|c|d, c→a|b|d, d→a|b|c)
   * 5 collaborators: max(1, 5 * 4) - 1 = 19  (20 messages: a→b|c|d|e, b→a|c|d|e, c→a|b|d|e, d→a|b|c|e, e→a|b|c|d)
   */
  public calculateCollaboratorIntervalMultiplier(numCollaborators: number): number {
    return Math.max(1, numCollaborators * (numCollaborators - 1)) - 1;
  }

  /**
   * Calculates the throttle interval based on number of collaborators.
   * Uses calculateCollaboratorIntervalMultiplier to determine the base scaling,
   * then multiplies by 100ms (base unit) and clamps between MIN_THROTTLE_INTERVAL and MAX_THROTTLE_INTERVAL.
   *
   * Examples of throttle intervals:
   * 1 collaborator: 0 * 100ms = 0ms (clamped to MIN_THROTTLE_INTERVAL)
   * 2 collaborators: 1 * 100ms = 100ms
   * 3 collaborators: 5 * 100ms = 500ms
   * 4 collaborators: 11 * 100ms = 1100ms
   * 5 collaborators: 19 * 100ms = 1900ms (clamped to MAX_THROTTLE_INTERVAL)
   */
  private calculateThrottleInterval(): number {
    const numCollaborators = this.getNumberOfCollaborators();
    const collaboratorIntervalMultiplier =
      this.calculateCollaboratorIntervalMultiplier(numCollaborators);
    const calculatedInterval = collaboratorIntervalMultiplier * 100; // base unit of 100ms
    return Math.min(
      Math.max(calculatedInterval, this.MIN_THROTTLE_INTERVAL),
      this.MAX_THROTTLE_INTERVAL,
    );
  }

  /**
   * Checks if a channel should be throttled
   */
  private shouldThrottleChannel(channel: AwarenessChannel): boolean {
    return this.THROTTLED_CHANNELS.has(channel);
  }

  /**
   * Determines whether to use binary encoding for a given channel.
   * This allows for gradual rollout and backwards compatibility.
   */
  public getEncodingTypeForChannel(channel: AwarenessChannel): AwarenessEncodingType {
    // Only the transient awareness channel has support for binary encoding at the moment
    if ((channel & AwarenessChannel.TransientTransaction) !== 0) {
      return AwarenessEncodingType.Binary;
    }
    return AwarenessEncodingType.String;
  }

  /**
   * Encodes an awareness update for a given channel and client IDs
   */
  private encodeAwarenessUpdate(
    channel: AwarenessChannel,
    clientIds: Set<number>,
    explicitEncodingType: AwarenessEncodingType,
  ): Uint8Array | undefined {
    const awareness = this.getAwareness(channel);
    const filteredClientIds = [...clientIds].filter((clientId) =>
      awareness.meta.has(clientId),
    );

    if (filteredClientIds.length === 0) {
      return undefined;
    }

    return this.dataHandler.encodeUpdate(
      awareness,
      channel,
      filteredClientIds,
      explicitEncodingType, // Specify encoding type for the update to be used by the receiver
    );
  }

  /**
   * Encodes an awareness update and adds it to the message data if successful
   */
  private addEncodedUpdateToMessage(
    message: AwarenessMessage,
    channel: AwarenessChannel,
    clientIds: Set<number>,
  ): void {
    const encodingType = this.getEncodingTypeForChannel(channel);
    const encoded = this.encodeAwarenessUpdate(channel, clientIds, encodingType);

    if (encoded) {
      const messageKey = this.getAwarenessMessageKey(channel);
      message.data[messageKey] = encoded;
      // Set the encoding type for the message using short form keys
      if (!message.encodingTypes) {
        message.encodingTypes = {};
      }
      // Use MessageKey enum values for short form keys
      const channelEncodingKey = this.getAwarenessMessageKeyShort(channel);
      message.encodingTypes[channelEncodingKey] = encodingType;
    }
  }

  /**
   * Returns the short form awareness message key for a given awareness channel.
   */
  private getAwarenessMessageKeyShort(
    channel: AwarenessChannel,
  ): keyof NonNullable<AwarenessMessage['encodingTypes']> {
    switch (channel) {
      case AwarenessChannel.TransientTransaction:
        return MessageKey.transientTransactionData;
      case AwarenessChannel.Telepointer:
        return MessageKey.telepointerData;
      case AwarenessChannel.Participants:
        return MessageKey.participantData;
      case AwarenessChannel.Selection:
        return MessageKey.selectionData;
      case AwarenessChannel.SmartLinks:
        return MessageKey.smartLinksUpdateData;
      case AwarenessChannel.Camera:
        return MessageKey.cameraData;
      case AwarenessChannel.Typing:
        return MessageKey.typingData;
      case AwarenessChannel.Racing:
        return MessageKey.racingData;
      case AwarenessChannel.Slides:
        return MessageKey.slidesData;
    }
  }

  /**
   * Returns an awareness message for the given awareness channels and client IDs.
   */
  public getAwarenessMessage(
    awarenessChannelClientIdMap: Map<AwarenessChannel, Set<number>>,
    capabilities?: Capabilities,
    bypassChannelThrottle = false,
  ): AwarenessMessage {
    const message: AwarenessMessage = {
      type: MessageType.Awareness,
      data: {},
    };

    // Split updates into throttled and non-throttled
    const throttledUpdates = new Map<AwarenessChannel, Set<number>>();

    for (const [channel, clientIds] of awarenessChannelClientIdMap) {
      if (!this.filterMessage(channel, false, capabilities)) {
        continue;
      }

      if (!bypassChannelThrottle && this.shouldThrottleChannel(channel)) {
        throttledUpdates.set(channel, clientIds);
      } else {
        // Process non-throttled updates immediately
        this.addEncodedUpdateToMessage(message, channel, clientIds);
      }
    }

    // Process throttled updates if enough time has passed
    const now = Date.now();
    const throttleInterval =
      throttledUpdates.size > 0 ? this.calculateThrottleInterval() : 0;

    if (
      throttledUpdates.size > 0 &&
      now - this.lastThrottledUpdateTime >= throttleInterval
    ) {
      for (const [channel, clientIds] of throttledUpdates) {
        this.addEncodedUpdateToMessage(message, channel, clientIds);
      }
      this.lastThrottledUpdateTime = now;
    }

    return message;
  }

  /**
   * Event handler that can be called to propagate an awareness message to all relevant
   * awareness objects.
   */
  public onAwareness = (
    message: AwarenessMessage,
    origin: TransactionOrigin,
    capabilities?: Capabilities,
  ) => {
    for (const key in message.data) {
      const channel = this.getAwarenessChannel(key);

      if (channel == null) {
        continue;
      }

      if (!this.filterMessage(channel, true, capabilities)) {
        continue;
      }

      const data = message.data[key as keyof AwarenessMessage['data']];
      if (data == null || !(data instanceof Uint8Array)) {
        continue;
      }

      // Get encoding type (binary or string) for the channel
      const channelEncodingKey = this.getAwarenessMessageKeyShort(channel);
      const encodingType = message.encodingTypes?.[channelEncodingKey];

      this.dataHandler.decodeUpdate(
        this.getAwareness(channel),
        channel,
        data,
        origin,
        encodingType,
      );
    }
  };

  /**
   * Returns the local state of the given awareness channels. Maps each awareness channel
   * to the local client ID, if it has any local state.
   */
  public getLocalState(
    channelMask: AwarenessChannel,
  ): Map<AwarenessChannel, Set<number>> | null {
    const result: Map<AwarenessChannel, Set<number>> = new Map();

    for (const channel of allAwarenessChannels) {
      if ((channelMask & channel) === 0) {
        continue;
      }

      const awareness = this.getAwareness(channel);
      if (awareness.getLocalState() !== null) {
        result.set(channel, new Set([awareness.clientID]));
      }
    }

    if (result.size > 0) {
      return result;
    }

    return null;
  }

  /**
   * Returns the local state of all awareness channels. Maps each awareness channel to
   * the local client ID, if it has any local state.
   */
  public getLocalStateForAllChannels(): Map<AwarenessChannel, Set<number>> | null {
    return this.getLocalState(allAwarenessChannelsMask);
  }

  /**
   * Removes all other users from all awareness channels.
   */
  public removeRemoteState(origin: TransactionOrigin) {
    for (const awareness of this.allAwareness) {
      awarenessProtocol.removeAwarenessStates(
        awareness.awareness,
        Array.from(awareness.awareness.getStates().keys()).filter(
          (client) => client !== awareness.awareness.clientID,
        ),
        new YjsOrigin(YJS_DEFAULT, origin),
      );
    }
  }

  /**
   * Removes the current user from all awareness channels.
   */
  public removeLocalState(origin: TransactionOrigin) {
    for (const awareness of this.allAwareness) {
      awarenessProtocol.removeAwarenessStates(
        awareness.awareness,
        [awareness.awareness.clientID],
        new YjsOrigin(YJS_DEFAULT, origin),
      );
    }
  }

  public destroy() {
    this.events.removeAllListeners();

    for (const awareness of this.allAwareness) {
      awareness.destroy();
    }

    for (const listener of this.destroyListeners) {
      listener();
    }
  }

  public onDestroy(listener: VoidFunction) {
    this.destroyListeners.push(listener);
  }
}
